export type CrowdLevelString =
  | 'LEVEL_1_VACANT'
  | 'LEVEL_2_MODERATE'
  | 'LEVEL_3_BUSY'
  | 'LEVEL_4_VERY_BUSY';

export interface HourlyForecast {
  timeStart: string;
  timeEnd: string;
  predictedEntries: number;
  predictedExits: number | null;
  crowdLevel: CrowdLevelString;
}

export interface DailyForecast {
  date: string;
  predictedEntries: number;
  predictedExits: number | null;
  hourlyForecasts: HourlyForecast[];
}

export interface VisitationForecastResponse {
  mpId: string;
  countryCode: string;
  forecastDate: string;
  dailyForecast: DailyForecast;
}

export const CROWD_LEVEL_MAP: Record<CrowdLevelString, number> = {
  LEVEL_1_VACANT: 1,
  LEVEL_2_MODERATE: 2,
  LEVEL_3_BUSY: 3,
  LEVEL_4_VERY_BUSY: 4,
};

export const getVisitationForecast = async (
  mpId: string,
  forecastDate: string,
): Promise<VisitationForecastResponse | null> => {
  try {
    const response = await fetch(
      `/api/app-wrapper?action=visitation-forecast&mpId=${encodeURIComponent(mpId)}&forecastDate=${encodeURIComponent(forecastDate)}`,
    );

    if (!response.ok) {
      console.error(
        `[Visitation Forecast] Request failed with status ${response.status}`,
      );
      return null;
    }

    return await response.json();
  } catch (error) {
    console.error('[Visitation Forecast] Fetch error:', error);
    return null;
  }
};

/**
 * Returns the crowd level (1-4) for the current hour based on the forecast.
 * Returns null if data is unavailable.
 */
export const getCurrentHourCrowdLevel = (
  forecast: VisitationForecastResponse,
  timezone?: string,
): number | null => {
  const now = timezone
    ? new Date(new Date().toLocaleString('en-US', { timeZone: timezone }))
    : new Date();

  const currentHour = now.getHours();
  const hourlyForecasts = forecast.dailyForecast?.hourlyForecasts;

  if (!hourlyForecasts?.length) return null;

  const match = hourlyForecasts.find((h) => {
    const startHour = parseInt(h.timeStart.split(':')[0], 10);
    return startHour === currentHour;
  });

  if (!match) return null;

  return CROWD_LEVEL_MAP[match.crowdLevel] ?? null;
};
